/**
 * @fileoverview What the author is told after pressing Publish.
 *
 * THIS IS WHERE THE SIZE REFUSAL FINALLY HAS SOMEWHERE TO BE SEEN. The publish
 * path has always measured a page against the relay's ceiling and refused it
 * rather than letting the socket close silently -- but until there was an
 * editor, that refusal was a value returned to nobody. A refusal an author
 * cannot read is not much better than the socket close it replaced.
 *
 * SO EVERY OUTCOME IS SPELLED OUT, IN THE AUTHOR'S TERMS, AND THEY STAY
 * DISTINCT. This is the deliberate opposite of the read path, which collapses
 * every failure into one indistinguishable "not available" so that a private
 * page cannot be told apart from a missing one. Nobody's privacy is protected
 * by being vague with an author about their own page; being vague just leaves
 * them pressing the same button.
 *
 * TWO THINGS EVERY FAILURE MESSAGE HERE SAYS. What happened, and that their
 * work is still in front of them. An author who thinks a refusal ate their
 * afternoon will close the tab, which is the one action that actually loses it.
 */

import { Link } from 'react-router-dom';

import { pagePath } from '@/services/nostr/pageLinks';
import type { PublishOutcome } from '@/services/pages/publishPage';

/** Bytes as something a person can compare: "412 KB", not "421,888". */
function readableSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function PublishResult({ outcome }: { outcome: PublishOutcome }) {
  if (outcome.status === 'published') {
    return (
      <div className="editor-result editor-result-good" role="status">
        <p>
          Published. Its address is permanent, so this link keeps working after every
          later edit.
        </p>
        <p>
          <Link to={pagePath({ ...outcome.address, relays: outcome.acceptedBy })}>
            View the page
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="editor-result editor-result-bad" role="alert">
      {outcome.status === 'too-large' ? (
        <>
          <p>
            This page is too big for the relay to accept. It is {readableSize(outcome.bytes)}{' '}
            and the most the relay will take in one message is{' '}
            {readableSize(outcome.limit)}.
          </p>
          <p>
            {/* The distinction matters to what the author should DO. A relay's own
                figure is exact and worth editing down to. An assumed one is a
                common default that this particular relay never confirmed, so a
                page just over it might well have been accepted. */}
            {outcome.advertised
              ? 'That is the limit the relay publishes for itself.'
              : 'The relay does not publish a limit, so that is the usual default assumed for it.'}
          </p>
          <p>
            Nothing was sent and nothing was lost. Shorten the page, or split it into
            two and link them together.
          </p>
        </>
      ) : outcome.status === 'slug-taken' ? (
        <>
          <p>
            You already have a page whose address ends in{' '}
            <code>{outcome.identifier}</code>, so this one would take a name that is
            spoken for.
          </p>
          <p>
            Two pages under one name make the readable link{' '}
            <code>/&lt;you&gt;/{outcome.identifier}</code> ambiguous, which is why this
            is refused rather than allowed to happen quietly. Give this page a
            different address, or{' '}
            <Link to={pagePath(outcome.conflictsWith)}>open the page that has it</Link>.
          </p>
        </>
      ) : outcome.status === 'name-unchecked' ? (
        <>
          <p>
            Not published. These relays did not answer, so there was no way to know whether you
            already have a page at <code>{outcome.identifier}</code>:
          </p>
          <ul className="editor-refusals">
            {outcome.relays.map((relay) => (
              <li key={relay}>
                <code>{relay}</code>
              </li>
            ))}
          </ul>
          <p>
            Publishing anyway could have replaced that page. Nothing was uploaded or signed, and
            your page is still here. Try again when the relay is back.
          </p>
        </>
      ) : outcome.status === 'upload-failed' ? (
        <>
          <p>
            The page&rsquo;s file could not be stored anywhere, so no page was published.
            Publishing one now would have pointed readers at bytes no server holds.
          </p>
          <ul className="editor-refusals">
            {outcome.refused.map((server) => (
              <li key={server}>{server}</li>
            ))}
          </ul>
        </>
      ) : outcome.status === 'rejected' ? (
        <>
          <p>No relay accepted the page. Each one&rsquo;s own words:</p>
          <ul className="editor-refusals">
            {outcome.refused.map((r) => (
              <li key={r.relay}>
                <code>{r.relay}</code> — {r.reason}
              </li>
            ))}
          </ul>
          <p>Your page is still here. Nothing was lost.</p>
        </>
      ) : outcome.status === 'key-required' ? (
        <>
          <p>
            This page is private, and saving it needs the key it was published under. It was not
            saved, and nothing about it has changed.
          </p>
          <p>
            Saving without the key would lock out everyone you shared it with, without saying so.
            Open the page again from its address so its key can be loaded, then save.
          </p>
        </>
      ) : (
        <>
          <p>{outcome.reason}</p>
          <p>Your page is still here. Nothing was lost.</p>
        </>
      )}
    </div>
  );
}
